import { useEffect, useRef, useState, useCallback } from 'react';

export default function TextPressure({
  text = 'Hello',
  fontFamily = 'Inter',
  fontWeight = 300,
  weight = true,
  width = true,
  italic = false,
  stroke = false,
  strokeWidth = 2,
  strokeColor = '#ffffff',
  textColor = '#ffffff',
  fontSize = 64,
  minFontSize = 24,
  className = '',
  style = {},
}) {
  const containerRef = useRef(null);
  const spansRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0, active: false });
  const rafRef = useRef(null);
  const currentVals = useRef([]);

  const setSpanRef = useCallback((el, i) => {
    spansRef.current[i] = el;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMove = (e) => {
      const rect = container.getBoundingClientRect();
      let x, y;
      if (e.touches) {
        x = e.touches[0].clientX - rect.left;
        y = e.touches[0].clientY - rect.top;
      } else {
        x = e.clientX - rect.left;
        y = e.clientY - rect.top;
      }
      mouseRef.current = { x, y, active: true };
    };

    const handleLeave = () => {
      mouseRef.current = { ...mouseRef.current, active: false };
    };

    container.addEventListener('mousemove', handleMove);
    container.addEventListener('touchmove', handleMove, { passive: true });
    container.addEventListener('mouseleave', handleLeave);
    container.addEventListener('touchend', handleLeave);

    const animate = () => {
      const spans = spansRef.current;
      const { x: mx, y: my, active } = mouseRef.current;

      for (let i = 0; i < spans.length; i++) {
        const span = spans[i];
        if (!span) continue;

        if (!currentVals.current[i]) {
          currentVals.current[i] = { wght: fontWeight, scale: 1 };
        }
        const cur = currentVals.current[i];

        let targetWght = fontWeight;
        let targetScale = 1;

        if (active) {
          const rect = span.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const cx = rect.left - containerRect.left + rect.width / 2;
          const cy = rect.top - containerRect.top + rect.height / 2;
          const dx = mx - cx;
          const dy = my - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxDist = 150;
          const proximity = Math.max(0, 1 - dist / maxDist);

          targetWght = weight ? fontWeight + proximity * (900 - fontWeight) : fontWeight;
          targetScale = width ? 1 + proximity * 0.15 : 1;
        }

        // Smooth interpolation
        cur.wght += (targetWght - cur.wght) * 0.15;
        cur.scale += (targetScale - cur.scale) * 0.15;

        span.style.fontWeight = Math.round(cur.wght);
        span.style.transform = `scaleX(${cur.scale.toFixed(3)})`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      container.removeEventListener('mousemove', handleMove);
      container.removeEventListener('touchmove', handleMove);
      container.removeEventListener('mouseleave', handleLeave);
      container.removeEventListener('touchend', handleLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [fontWeight, weight, width]);

  const chars = text.split('');

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        display: 'inline-block',
        cursor: 'default',
        ...style,
      }}
    >
      <span
        style={{
          fontFamily,
          fontSize,
          fontWeight,
          color: textColor,
          display: 'inline-flex',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          WebkitTextStroke: stroke ? `${strokeWidth}px ${strokeColor}` : undefined,
          paintOrder: stroke ? 'stroke fill' : undefined,
        }}
      >
        {chars.map((ch, i) => (
          <span
            key={i}
            ref={(el) => setSpanRef(el, i)}
            style={{
              fontFamily,
              fontWeight,
              display: 'inline-block',
              willChange: 'font-weight, transform',
            }}
          >
            {ch === ' ' ? '\u00A0' : ch}
          </span>
        ))}
      </span>
    </div>
  );
}
